import React from 'react';
import useFetchCampaignKpiCards from '../hooks/useFetchCampaignKpiCards';
import {
  formatCamelCaseToCapitalized,
  formatNumberWithCommas,
} from '../../utilities/helpers';
import { Spinner } from '../../../../../Components';
import { Chip, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  cardsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '16px',
    position: 'relative',
    padding: '16px',
  },
  card: {
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    flex: '1 1 calc(33.333% - 16px)',
    minWidth: '200px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  platformChip: {
    background: '#000000',
    color: '#fff',
  },
  pointer: {
    cursor: 'pointer',
  },
  topMediaTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  topMediaSubtext: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
  },
}));

const clickableFields = ['activeCampaigns', 'topPerformingMedia'];

const KPICard = ({ params, setParams }) => {
  const { data: kpiData, loading: kpiLoading } = useFetchCampaignKpiCards(params);
  const classes = useStyles();
  const hasPointerCursor = (field) => clickableFields.includes(field);

  const onCardClickHandler = (field, fieldValue) => {
    if (clickableFields.includes(field)) {
      let key, value;
      if (field === 'activeCampaigns') {
        key = 'isActive';
        value = true;
      }
      if (field === 'topPerformingPlatform') {
        key = 'platform';
        value = fieldValue;
      }
      if (field === 'topPerformingMedia') {
        key = 'platform';
        value = fieldValue?.mediaDetails || fieldValue;
      }
      setParams({
        ...params,
        [key]: value,
      });
    }
  };

  const transformedKpiData = {
    ...kpiData,
    topPerformingMedia: kpiData?.mediaDetailsData
      ? {
        mediaDetails: kpiData.mediaDetailsData.mediaDetails,
        qualifiedLeads: kpiData.mediaDetailsData.qualifiedLeads,
        totalLeads: kpiData.mediaDetailsData.totalLeads,
        qualificationRate: kpiData.mediaDetailsData.qualificationRate,
      }
      : null,
  };

  return (
    <div className={classes.cardsContainer}>
      {kpiLoading ? (
        <Spinner isAbsolute isActive={kpiLoading} />
      ) : (
        Object.entries(transformedKpiData).map(([key, value]) => {
          if (key === 'topPerformingMedia') {
            return (
              <div
                key={key}
                className={clsx(classes.card, {
                  [classes.pointer]: hasPointerCursor(key),
                })}
                onClick={() => onCardClickHandler(key, value)}
              >
                <h5>{formatCamelCaseToCapitalized(key)}</h5>
                <div>
                  {value && value.mediaDetails ? (
                    <>
                      <Typography variant="h3" className={classes.topMediaTitle}>
                        {value.mediaDetails}
                      </Typography>
                      <strong>
                        Qualification Rate: {(value.qualificationRate * 100).toFixed(1)}%
                      </strong>
                      <Typography variant="body2" className={classes.topMediaSubtext}>
                        ({value.qualifiedLeads} Qualified / {value.totalLeads} Total)
                      </Typography>
                    </>
                  ) : (
                    <h3>
                      <strong>--</strong>
                    </h3>
                  )}
                </div>
              </div>
            );
          }

          if (key === 'mediaDetailsData') {
            return null;
          }

          if (typeof value === 'object' && value !== null) {
            return null;
          }

          return (
            <div
              key={key}
              className={clsx(classes.card, {
                [classes.pointer]: hasPointerCursor(key),
              })}
              onClick={() => onCardClickHandler(key, value)}
            >
              <h5>{formatCamelCaseToCapitalized(key)}</h5>
              <h3>
                {typeof value === 'number' ? (
                  <strong>{formatNumberWithCommas(value)}</strong>
                ) : (
                  <Chip label={value} className={classes.platformChip} />
                )}
              </h3>
            </div>
          );
        })
      )}
    </div>
  );
};

export default KPICard;